import i18n from 'i18next'
import React from 'react'
import { useMountedState } from 'react-use'

import { graphql } from '~/gql'
import { useInvalidateBrandAccountAiEnabledQuery } from '~/hooks/useBrandAccountAiEnabledQuery'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { toOutputLanguage } from '~/locales'
import { exists, isAiDisabledError } from '~/utils'

const askDocumentQuestionMutationDocument = graphql(`
  mutation DocumentQaPage_AskDocumentQuestion(
    $brandAccountId: ID!
    $question: String!
    $filters: DocumentQaFiltersInput
    $outputLanguage: OutputLanguage!
  ) {
    askDocumentQuestion(
      brandAccountId: $brandAccountId
      question: $question
      filters: $filters
      outputLanguage: $outputLanguage
    ) {
      requestId
    }
  }
`)

const documentQuestionStatusQueryDocument = graphql(`
  query DocumentQaPage_DocumentQuestionStatus($requestId: ID!) {
    documentQuestionStatus(requestId: $requestId) {
      arrived
      error
      result {
        matchedSections {
          documentTitle
          excerpt
          reasoning
          restoreKey
          sectionNumber
        }
      }
    }
  }
`)

type MatchedSection = {
  documentTitle: string
  excerpt: string
  reasoning: string
  restoreKey: string
  sectionNumber: number
}

// Hook-produced errors are stored as i18n keys, not translated strings — a
// string translated at resolve time would stay in whichever language was
// active when polling finished, ignoring later language switches.
type ErrorI18nKey =
  | 'ai_disabled_message'
  | 'an_error_has_occurred'
  | 'document_qa_no_candidates'
  | 'document_qa_request_not_found'
  | 'document_qa_timeout'

type AskDocumentQuestionResult = {
  error?: string
  errorI18nKey?: ErrorI18nKey
  matchedSections: MatchedSection[]
}

/**
 * Normalizes a GraphQL-returned matched section into the strict
 * MatchedSection shape used by the UI.
 *
 * @param s - Raw matched section from the GraphQL response.
 * @param s.documentTitle - Title of the source saved table/graph.
 * @param s.excerpt - Short excerpt of the matched passage.
 * @param s.reasoning - Model-generated rationale for the match.
 * @param s.restoreKey - Opaque key used to restore the saved table/graph.
 * @param s.sectionNumber - 1-based section number.
 * @returns Normalized MatchedSection.
 */
function mapSection(s: {
  documentTitle: string
  excerpt: string
  reasoning: string
  restoreKey: string
  sectionNumber: number
}): MatchedSection {
  return {
    documentTitle: s.documentTitle,
    excerpt: s.excerpt,
    reasoning: s.reasoning,
    restoreKey: s.restoreKey,
    sectionNumber: s.sectionNumber,
  }
}

export const useAskDocumentQuestion = () => {
  const graphQLRequest = useGraphQLRequest()
  const invalidateBrandAccountAiEnabledQuery =
    useInvalidateBrandAccountAiEnabledQuery()
  const isMounted = useMountedState()
  const [isLoading, setIsLoading] = React.useState(false)
  const [result, setResult] = React.useState<AskDocumentQuestionResult | null>(
    null,
  )
  const requestSeqRef = React.useRef(0)

  const pollForResult = React.useCallback(
    (requestId: string, seq: number) =>
      new Promise<AskDocumentQuestionResult | null>((resolve) => {
        const startTime = Date.now()
        const pollUntilArrived = async () => {
          try {
            if (requestSeqRef.current !== seq) {
              resolve(null)

              return
            }

            if (!isMounted()) {
              resolve(null)

              return
            }

            // Match the backend's STATUS_TIMEOUT_MS (10 min). A shorter FE
            // ceiling would abandon results that complete between 5-10 min.
            if (Date.now() - startTime > 600_000) {
              resolve({
                errorI18nKey: 'document_qa_timeout',
                matchedSections: [],
              })

              return
            }

            const queryResult = await graphQLRequest({
              document: documentQuestionStatusQueryDocument,
              variables: { requestId },
            })

            const status = queryResult.documentQuestionStatus
            if (status === null || status === undefined) {
              resolve({
                errorI18nKey: 'document_qa_request_not_found',
                matchedSections: [],
              })

              return
            }
            if (!status.arrived) {
              if (requestSeqRef.current !== seq) {
                resolve(null)

                return
              }

              setTimeout(pollUntilArrived, 2000)

              return
            }

            if (exists(status.error)) {
              // 'no_candidates' and 'timeout' are fixed sentinels the backend
              // stores on the tracking row. Anything else is an unmapped
              // provider error (e.g. a 429 with JSON) that rides on `data`, not
              // `errors`, so Apollo never masks it — fall back to the generic
              // key rather than letting it reach the UI verbatim.
              resolve({
                errorI18nKey:
                  status.error === 'no_candidates'
                    ? 'document_qa_no_candidates'
                    : status.error === 'timeout'
                      ? 'document_qa_timeout'
                      : 'an_error_has_occurred',
                matchedSections: [],
              })

              return
            }

            resolve({
              matchedSections: (status.result?.matchedSections ?? []).map(
                mapSection,
              ),
            })
          } catch {
            resolve({
              errorI18nKey: 'an_error_has_occurred',
              matchedSections: [],
            })
          }
        }
        pollUntilArrived()
      }),
    [graphQLRequest, isMounted],
  )

  const ask = React.useCallback(
    async ({
      onRequestId,
      ...variables
    }: {
      brandAccountId: string
      filters?: {
        brandGroupName?: string | null
        brandName?: string | null
        surveyTitle?: string | null
      }
      onRequestId?: (requestId: string) => void
      question: string
    }): Promise<string | null> => {
      const seq = ++requestSeqRef.current
      setIsLoading(true)
      setResult(null)

      try {
        const mutationResult = await graphQLRequest({
          document: askDocumentQuestionMutationDocument,
          variables: {
            ...variables,
            outputLanguage: toOutputLanguage(i18n.language),
          },
        })

        const requestId = mutationResult.askDocumentQuestion?.requestId
        if (!exists(requestId)) {
          return null
        }

        if (requestSeqRef.current === seq) {
          onRequestId?.(requestId)
        }

        const pollResult = await pollForResult(requestId, seq)

        if (isMounted() && requestSeqRef.current === seq) {
          setResult(pollResult)
        }

        return requestId
      } catch (error) {
        // The poll phase resolves its own errors as an {error} value, but a
        // throw from the initial mutation would otherwise escape as an
        // unhandled rejection with a silent UI. Surface it as the same domain
        // value shape so the page can render it.
        const aiDisabled = isAiDisabledError(error)

        if (aiDisabled) {
          // Reaching the server gate means the cached aiEnabled flag still said
          // "on" (staleTime keeps it cached across a toggle made in another tab,
          // and nothing remounts this page to refetch it). Drop that cache so the
          // form switches to its disabled state instead of letting the next click
          // hit the same rejection.
          void invalidateBrandAccountAiEnabledQuery()
        }

        if (isMounted() && requestSeqRef.current === seq) {
          setResult({
            // ClientError.message is the whole response JSON — never show it.
            errorI18nKey: aiDisabled
              ? 'ai_disabled_message'
              : 'an_error_has_occurred',
            matchedSections: [],
          })
        }

        return null
      } finally {
        if (isMounted() && requestSeqRef.current === seq) {
          setIsLoading(false)
        }
      }
    },
    [
      graphQLRequest,
      invalidateBrandAccountAiEnabledQuery,
      isMounted,
      pollForResult,
    ],
  )

  const rehydrate = React.useCallback(
    async (requestId: string) => {
      const seq = ++requestSeqRef.current
      setIsLoading(true)

      try {
        const pollResult = await pollForResult(requestId, seq)

        if (isMounted() && requestSeqRef.current === seq) {
          setResult(pollResult)
        }
      } finally {
        if (isMounted() && requestSeqRef.current === seq) {
          setIsLoading(false)
        }
      }
    },
    [isMounted, pollForResult],
  )

  const clearResult = React.useCallback(() => {
    requestSeqRef.current++
    setResult(null)
    setIsLoading(false)
  }, [])

  return { ask, clearResult, isLoading, rehydrate, result }
}
